import { useState, useMemo, useCallback, ChangeEvent } from 'react'

const filterNumber = (value: string) => value.replace(/[^0-9]/g, '')

type UseTelInputParams = {
  maxLengthLeft: number
  maxLengthCenter: number
  maxLengthRight: number
  initialValue?: string
}

export const useTelInput = ({
  maxLengthLeft,
  maxLengthCenter,
  maxLengthRight,
  initialValue = '',
}: UseTelInputParams) => {
  const [left, setLeft] = useState(initialValue.slice(0, maxLengthLeft))
  const [center, setCenter] = useState(
    initialValue.slice(maxLengthLeft, maxLengthLeft + maxLengthCenter)
  )
  const [right, setRight] = useState(initialValue.slice(maxLengthLeft + maxLengthCenter))

  const value = `${left}${center}${right}`

  const maxLengthTotal = useMemo(
    () => maxLengthLeft + maxLengthCenter + maxLengthRight,
    [maxLengthLeft, maxLengthCenter, maxLengthRight]
  )

  const canClickEnter = value.length === maxLengthTotal

  const onClickNumber = useCallback(
    (num: string) => {
      if (left.length < maxLengthLeft) {
        setLeft((prev) => `${prev}${num}`)
      } else if (center.length < maxLengthCenter) {
        setCenter((prev) => `${prev}${num}`)
      } else if (right.length < maxLengthRight) {
        setRight((prev) => `${prev}${num}`)
      }
    },
    [left.length, center.length, right.length, maxLengthLeft, maxLengthCenter, maxLengthRight]
  )

  const onClickBackSpace = useCallback(() => {
    if (right.length > 0) {
      setRight((prev) => prev.substring(0, prev.length - 1))
    } else if (center.length > 0) {
      setCenter((prev) => prev.substring(0, prev.length - 1))
    } else {
      setLeft((prev) => prev.substring(0, prev.length - 1))
    }
  }, [right.length, center.length])

  const onChangeLeft = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setLeft(filterNumber(e.target.value))
  }, [])

  const onChangeCenter = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setCenter(filterNumber(e.target.value))
  }, [])

  const onChangeRight = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setRight(filterNumber(e.target.value))
  }, [])

  return {
    left,
    center,
    right,
    value,
    canClickEnter,
    maxLengthTotal,
    onClickNumber,
    onClickBackSpace,
    onChangeLeft,
    onChangeCenter,
    onChangeRight,
  }
}
